import type { Ref } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import type { TreeItemContentProps } from '@mui/x-tree-view';
import { useTreeItemState } from '@mui/x-tree-view';
import clsx from 'clsx';

import type { GroupedCodingResult, SentimentCode } from 'src/types/dashboard';
import { OPEN_COMMENTS_TREE_ITEM_GRID_TEST_ID } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/TreeItem/index';
import SentimentBarSection from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/TreeItem/SentimentBarSection';
import type { SentimentFilter } from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import SentimentTreeLabel from './SentimentTreeLabel';

import { styles } from './index.styles';

export const RELEVANCE_ICON_TEST_ID = 'relevance-icon';
export const handleSentimentSelection = (
  sentimentFilter: SentimentFilter,
  treeData: GroupedCodingResult,
  handleSelectionClick: (
    topicSentimentId: null | string,
    sentiment?: SentimentCode,
  ) => void,
) => {
  const sentimentMapping: {
    [key: string]: {
      topicSentimentId: string | null;
      sentimentValue: SentimentCode;
    };
  } = {
    // Mocked data is static, so count values of comments shown on SentimentBar on local env are NOT accurate.
    positiveCount: { topicSentimentId: treeData.positiveId, sentimentValue: 1 },
    neutralCount: { topicSentimentId: treeData.neutralId, sentimentValue: 0 },
    negativeCount: {
      topicSentimentId: treeData.negativeId,
      sentimentValue: -1,
    },
  };

  const { topicSentimentId, sentimentValue } = sentimentMapping[
    sentimentFilter as SentimentFilter
  ] || { topicSentimentId: null, sentimentValue: undefined };

  handleSelectionClick(topicSentimentId, sentimentValue);
};

export type SentimentBarProps = {
  handleSelectionClick: (
    topicSentimentId: null | string,
    sentiment?: SentimentCode,
  ) => void;
} & TreeItemContentProps;

const SentimentBar = React.forwardRef(function CustomContent(
  props: SentimentBarProps,
  ref: React.ForwardedRef<HTMLLIElement>,
) {
  const {
    classes,
    className,
    label,
    itemId,
    icon: iconProp,
    expansionIcon,
    displayIcon,
    treeData,
    sentimentFilter,
    highestCount,
    level,
    handleSelectionClick,
  } = {
    ...props,
  };
  const {
    disabled,
    expanded,
    selected,
    focused,
    handleExpansion,
    preventSelection,
  } = useTreeItemState(itemId);
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  // Check which of the three bars should be displayed. Determined by the string in the 'sentimentFilter' property.

  const showNeutralBar =
    !!treeData.neutralCount &&
    (sentimentFilter === 'totalOfAllCounts' ||
      sentimentFilter === 'neutralCount');

  const showPositiveBar =
    !!treeData.positiveCount &&
    (sentimentFilter === 'totalOfAllCounts' ||
      sentimentFilter === 'positiveCount');

  const showNegativeBar =
    !!treeData.negativeCount &&
    (sentimentFilter === 'totalOfAllCounts' ||
      sentimentFilter === 'negativeCount');

  // Create Tooltip information.
  const tooltip = [
    `${t('OpenComments.negativeTopics')}: ${treeData.negativeCount}`,
    `${t('OpenComments.neutralTopics')}: ${treeData.neutralCount}`,
    `${t('OpenComments.positiveTopics')}: ${treeData.positiveCount}`,
  ].join(' | ');
  const isRootLevel = level < 1;

  const icon = iconProp || expansionIcon || displayIcon;
  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    preventSelection(event);
  };

  return (
    <div
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onMouseDown={handleMouseDown}
      ref={ref as Ref<HTMLDivElement>}
    >
      <Grid container sx={{ alignItems: 'center' }}>
        <SentimentTreeLabel
          label={label}
          treeData={treeData}
          handleSelectionClick={handleSelectionClick}
          sentimentFilter={sentimentFilter}
          labelClasses={classes.label}
          icon={icon}
          handleExpansionClick={handleExpansion}
        />

        <Grid
          item
          xs={12}
          md={9}
          data-test-id={OPEN_COMMENTS_TREE_ITEM_GRID_TEST_ID}
        >
          <Tooltip
            arrow
            enterTouchDelay={1}
            followCursor
            leaveTouchDelay={3000}
            placement="top"
            title={tooltip}
          >
            <Box
              sx={{
                pl: !isRootLevel && !upMd ? 2 : !isRootLevel ? '2px' : 0,
                ...styles.stackedBar,
              }}
            >
              {showNegativeBar && (
                <SentimentBarSection
                  count={treeData.negativeCount}
                  highestCount={highestCount}
                  isRootLevel={isRootLevel}
                  handleClick={handleSelectionClick}
                  id={treeData.negativeId}
                  color={theme.colors.commentNegative}
                  sentiment={-1}
                />
              )}

              {showNeutralBar && (
                <SentimentBarSection
                  count={treeData.neutralCount}
                  highestCount={highestCount}
                  isRootLevel={isRootLevel}
                  handleClick={handleSelectionClick}
                  id={treeData.neutralId}
                  color={theme.colors.commentNeutral}
                  sentiment={0}
                />
              )}

              {showPositiveBar && (
                <SentimentBarSection
                  count={treeData.positiveCount}
                  highestCount={highestCount}
                  isRootLevel={isRootLevel}
                  handleClick={handleSelectionClick}
                  id={treeData.positiveId}
                  color={theme.colors.commentPositive}
                  sentiment={1}
                />
              )}
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </div>
  );
});

export default SentimentBar;
